import { IonAlert, IonButton, IonContent, IonHeader, IonLoading, IonPage } from "@ionic/react";
import { RouteComponentProps } from "react-router";
import "./Landing.css";
import Toolbar from "../components/Toolbar";
import Footer from "../components/Footer";
import { useContext, useEffect, useRef, useState } from "react";
import { Report } from "../utils/Interfaces";
import ReportModal from "../components/ReportModal";
import { MAPS_API_KEY } from "../utils/Constants";
import GoogleMapReact from "google-map-react";
import useSupercluster from "use-supercluster";
import { BBox, GeoJsonProperties } from "geojson";
import AppContext from "../providers/AppContext";
import { APIService } from "../network/APIService";
import CustomTable from "../components/CustomTable";
import MapMarker from "../components/MapMarker";
import { PointFeature } from "supercluster";
import MapClusterMarker from "../components/MapClusterMarker";
import { UtilFunctions } from "../utils/UtilFunctions";
import { TABLE_COLUMNS_REPORTS } from "../utils/Columns";

const Landing: React.FC<RouteComponentProps> = props => {
  const appContext = useContext(AppContext);
  const [isInitializingData, setIsInitializingData] = useState(true);
  const [showFailedToFetchReportsModal, setShowFailedToFetchReportsModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report>();
  const [showReportModal, setShowReportModal] = useState(false);

  // map setup
  const defaultCenter = { lat: 54.3781, lng: -1.9 };
  const defaultZoom = 5.5;
  const mapRef = useRef<any>(null);
  const [zoom, setZoom] = useState(defaultZoom);
  const [bounds, setBounds] = useState<BBox>();
  const [points, setPoints] = useState<PointFeature<GeoJsonProperties>[]>([]);
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: {
      radius: 75, // size of the clustering zones. higher number equals less clusters overall
      maxZoom: 20,
      // minZoom: 5,
      // poin: 10,
    },
  });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    // verify user
    const verifyResponse = await APIService.verifyUser(appContext.firebaseUserLogInInfo!.uid, appContext.firebaseUserLogInInfo!.email!);
    if (verifyResponse) {
      // user verified, fetch reports and init other data
      const verifiedUser = verifyResponse.DBRecord[0];

      appContext.setJWT(verifyResponse.jwt);
      const mappedUser = UtilFunctions.mapUsers([verifiedUser])[0];
      appContext.setCurrentUser(mappedUser);

      const reports = await APIService.fetchReports(verifiedUser.id, verifyResponse.jwt);
      if (reports) {
        // successfully fetched reports

        // update global reports state
        appContext.setReports(reports);

        // update map points
        let newPoints: PointFeature<GeoJsonProperties>[] = [];
        reports.forEach(rep => {
          if (rep.latitude != 0 && rep.longitude != 0) {
            newPoints.push({
              type: "Feature",
              properties: {
                // can set these fields to whatever I want (e.g. can pass in report)
                cluster: false,
                report: rep,
              },
              geometry: {
                type: "Point",
                coordinates: [rep.longitude, rep.latitude],
              },
            });
          }
        });
        setPoints(newPoints);

        // close loading indicator
        setIsInitializingData(false);
      } else {
        // failed to fetch reports
        setShowFailedToFetchReportsModal(true);
        setIsInitializingData(false);
      }
    } else {
      // user not verified, send back to login
      props.history.push("/login");
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar props={props} />
      </IonHeader>
      <IonContent>
        <div className="pageContainer">
          <div className="pageContentContainer">
            <h1 id="landingHeader" className="pageHeader">
              Welcome back to the Safe Car Wash Admin Site
            </h1>
            <div className="map">
              <GoogleMapReact
                ref={mapRef}
                yesIWantToUseGoogleMapApiInternals
                bootstrapURLKeys={{ key: MAPS_API_KEY }}
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                onGoogleApiLoaded={({ map }) => {
                  mapRef.current = map;
                }}
                onChange={({ zoom, bounds }) => {
                  setZoom(zoom);
                  setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
                }}
              >
                {/* clusters/markers */}
                {clusters.map((clusterOrSinglePoint, index) => {
                  const [longitude, latitude] = clusterOrSinglePoint.geometry.coordinates;
                  const isCluster = clusterOrSinglePoint.properties?.cluster;
                  const pointCount = clusterOrSinglePoint.properties?.point_count;
                  const reportClicked = clusterOrSinglePoint.properties?.report;
                  let totalVisiblePointCount = 0;
                  clusters.forEach(c => (totalVisiblePointCount += typeof c.properties?.point_count === "number" ? c.properties?.point_count : 0));
                  if (isCluster) {
                    // return cluster
                    return (
                      <MapClusterMarker
                        key={index}
                        lat={latitude}
                        lng={longitude}
                        numOfClusters={clusters.length}
                        clusterPointCount={pointCount}
                        totalPointCount={points.length}
                        totalVisiblePointCount={totalVisiblePointCount}
                        handleClick={() => {
                          // expand cluster into multiple when clicked
                          const clusterId = Number.parseInt(clusterOrSinglePoint?.id!.toString());
                          const expansionZone = Math.min(supercluster!.getClusterExpansionZoom(clusterId), 20);
                          mapRef.current.setZoom(expansionZone);
                          mapRef.current.panTo({ lat: latitude, lng: longitude });
                        }}
                        currentZoom={mapRef.current.zoom}
                      />
                    );
                  } else {
                    // return individual marker
                    return (
                      <MapMarker
                        key={index}
                        lat={latitude}
                        lng={longitude}
                        handleClick={() => {
                          setSelectedReport(reportClicked);
                          setShowReportModal(true);
                        }}
                      />
                    );
                  }
                })}
              </GoogleMapReact>
            </div>
            <div id="landingSubheaderContainer">
              <h3 className="pageHeader inlineBlock">Most recent reports</h3>
              <div>
                <IonButton disabled={appContext.reports.length === 0} style={{ marginRight: "16px" }} routerLink="/locations" className="yellowBtn">
                  View locations
                </IonButton>
                <IonButton disabled={appContext.reports.length === 0} routerLink="/reports" className="yellowBtn">
                  View all reports
                </IonButton>
              </div>
            </div>
            <CustomTable
              cols={TABLE_COLUMNS_REPORTS}
              rowData={appContext.reports.slice(-10, appContext.reports.length)}
              handleSelectItem={report => {
                setSelectedReport(report);
                setShowReportModal(true);
              }}
              defaultSort={[{ id: "dateTimeCreated", desc: true }]}
            />
            <ReportModal selectedReport={selectedReport} showModal={showReportModal} setShowModal={show => setShowReportModal(show)} />
          </div>
          <Footer />
        </div>
        <IonLoading isOpen={isInitializingData} message={"Loading data..."} />
        <IonAlert
          isOpen={showFailedToFetchReportsModal}
          onDidDismiss={() => setShowFailedToFetchReportsModal(false)}
          header={"Alert"}
          message={"Failed to fetch reports. Please try again later."}
          buttons={["OK"]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Landing;
